'use client';
import { fireAndForget } from '@sandstone/shared-utils';
import { useState, useEffect, useCallback, useRef } from 'react';
import { extractDataLayerEventName } from './data-layer-event-name';
// ============================================================================
// Storage Keys
// ============================================================================
const STORAGE_KEY_EVENTS = 'dev-tools:analytics-events';
const STORAGE_KEY_SETTINGS = 'dev-tools:analytics-settings';
// PostHog managed reverse proxy — exact hostname match, not substring
const POSTHOG_PROXY_HOST = 'b.sandstone.com';
function isPosthogProxyUrl(url) {
    try {
        return new URL(url, window.location.origin).hostname === POSTHOG_PROXY_HOST;
    }
    catch {
        return false;
    }
}
// ============================================================================
// Default Event Schemas for Validation
// ============================================================================
const DEFAULT_SCHEMAS = [
    { eventName: 'page_view', requiredProperties: ['page_path', 'page_title'], source: 'gtm' },
    { eventName: '$pageview', requiredProperties: ['$current_url'], source: 'posthog' },
    { eventName: 'purchase', requiredProperties: ['transaction_id', 'value', 'currency'], source: 'gtm' },
    { eventName: 'sign_up', requiredProperties: ['method'] },
    { eventName: 'login', requiredProperties: ['method'] },
];
const DEFAULT_SETTINGS = {
    autoScroll: true,
    soundEnabled: false,
    alertPatterns: [],
    customSchemas: [],
    // Hide noisy events by default
    hideSnapshotEvents: true,
    hideSystemEvents: true,
    hideAutocaptureEvents: false,
};
export function categorizeEvent(eventName, source) {
    // Snapshot events (session replay)
    if (eventName === '$snapshot')
        return 'snapshot';
    // Pageview events
    if (['$pageview', '$pageleave', 'page_view', 'pageview'].includes(eventName)) {
        return 'pageview';
    }
    // Autocapture events
    if (eventName === '$autocapture' || eventName.startsWith('$rageclick')) {
        return 'autocapture';
    }
    // All other $ prefixed events are system/internal (PostHog)
    if (source === 'posthog' && eventName.startsWith('$')) {
        return 'system';
    }
    // GTM internal events
    if (source === 'gtm' && eventName.startsWith('gtm.')) {
        return 'system';
    }
    // Everything else is custom
    return 'custom';
}
// ============================================================================
// Persistence Helpers
// ============================================================================
function loadPersistedEvents() {
    if (typeof window === 'undefined')
        return [];
    try {
        const storedEvents = sessionStorage.getItem(STORAGE_KEY_EVENTS);
        if (storedEvents) {
            const parsed = JSON.parse(storedEvents);
            return parsed.map((e) => Object.assign({}, e, { timestamp: new Date(e.timestamp) }));
        }
    }
    catch (e) {
        console.warn('[AnalyticsViewer] Failed to load persisted events:', e);
    }
    return [];
}
function loadPersistedSettings() {
    if (typeof window === 'undefined')
        return DEFAULT_SETTINGS;
    try {
        const storedSettings = sessionStorage.getItem(STORAGE_KEY_SETTINGS);
        if (storedSettings) {
            return { ...DEFAULT_SETTINGS, ...JSON.parse(storedSettings) };
        }
    }
    catch (e) {
        console.warn('[AnalyticsViewer] Failed to load persisted settings:', e);
    }
    return DEFAULT_SETTINGS;
}
// ============================================================================
// Hook: useAnalyticsEvents
// ============================================================================
export function useAnalyticsEvents(maxEvents = 100) {
    // Initialize state with persisted data synchronously to avoid race conditions
    const [events, setEvents] = useState(loadPersistedEvents);
    const [isCapturing, setIsCapturing] = useState(true);
    const [settings, setSettings] = useState(loadPersistedSettings);
    const [networkRequests, setNetworkRequests] = useState([]);
    const audioRef = useRef(null);
    const audioContextRef = useRef(null);
    const hasInitializedRef = useRef(false);
    // Mark as initialized after first render
    useEffect(() => {
        hasInitializedRef.current = true;
    }, []);
    // Persist events to sessionStorage (only after initialization)
    useEffect(() => {
        if (!hasInitializedRef.current)
            return;
        try {
            sessionStorage.setItem(STORAGE_KEY_EVENTS, JSON.stringify(events.slice(-maxEvents)));
        }
        catch (e) {
            console.warn('[AnalyticsViewer] Failed to persist events:', e);
        }
    }, [events, maxEvents]);
    // Persist settings to sessionStorage (only after initialization)
    useEffect(() => {
        if (!hasInitializedRef.current)
            return;
        try {
            sessionStorage.setItem(STORAGE_KEY_SETTINGS, JSON.stringify(settings));
        }
        catch (e) {
            console.warn('[AnalyticsViewer] Failed to persist settings:', e);
        }
    }, [settings]);
    // Initialize audio for alerts
    useEffect(() => {
        // Create a simple beep sound using Web Audio API
        audioRef.current = null; // We'll use Web Audio API instead
    }, []);
    const playAlertSound = useCallback(() => {
        if (!settings.soundEnabled)
            return;
        try {
            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            // Reuse a single AudioContext to avoid exhausting browser limit (~6 concurrent)
            if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
                audioContextRef.current = new AudioContextClass();
            }
            const audioContext = audioContextRef.current;
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();
            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);
            oscillator.frequency.value = 800;
            oscillator.type = 'sine';
            gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
            oscillator.start(audioContext.currentTime);
            oscillator.stop(audioContext.currentTime + 0.2);
        }
        catch {
            // Audio not supported
        }
    }, [settings.soundEnabled]);
    // Check for duplicates
    const checkDuplicate = useCallback((eventName, source, recentEvents) => {
        const now = Date.now();
        const duplicateWindow = 1000; // 1 second window
        return recentEvents.some(e => e.eventName === eventName && e.source === source && now - e.timestamp.getTime() < duplicateWindow);
    }, []);
    // Validate event against schemas
    const validateEvent = useCallback((eventName, data, source) => {
        const allSchemas = [...DEFAULT_SCHEMAS, ...settings.customSchemas];
        const matchingSchemas = allSchemas.filter(s => s.eventName === eventName && (!s.source || s.source === source));
        const errors = [];
        for (const schema of matchingSchemas) {
            for (const prop of schema.requiredProperties) {
                if (!(prop in data) || data[prop] === undefined || data[prop] === null || data[prop] === '') {
                    errors.push(`Missing required property: ${prop}`);
                }
            }
        }
        return errors;
    }, [settings.customSchemas]);
    // Check if event matches alert patterns
    const checkAlertPatterns = useCallback((eventName) => {
        return settings.alertPatterns.some(pattern => {
            try {
                return new RegExp(pattern, 'i').test(eventName);
            }
            catch {
                return eventName.toLowerCase().includes(pattern.toLowerCase());
            }
        });
    }, [settings.alertPatterns]);
    // Try to capture trigger source from stack trace
    const captureTriggerSource = useCallback(() => {
        try {
            const stack = new Error().stack || '';
            const lines = stack.split('\n').slice(3); // Skip Error, captureTriggerSource, and capture method
            for (const line of lines) {
                // Look for app code, skip node_modules and internal
                if (line.includes('/app/') || line.includes('/components/') || line.includes('/pages/')) {
                    const match = line.match(/at\s+(\S+)/);
                    if (match)
                        return match[1];
                }
            }
            return undefined;
        }
        catch {
            return undefined;
        }
    }, []);
    // Add event helper - using ref to avoid effect re-runs
    // Initialize the ref with a function immediately (not in effect) to ensure it's available
    const addEventRef = useRef(() => {
        // This initial function is a no-op until the real one is ready
    });
    const addEvent = useCallback((source, eventName, data) => {
        setEvents(prev => {
            const isDuplicate = checkDuplicate(eventName, source, prev);
            const validationErrors = validateEvent(eventName, data, source);
            const triggerSource = captureTriggerSource();
            const newEvent = {
                id: `${source}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                timestamp: new Date(),
                source,
                eventName,
                data,
                isDuplicate,
                validationErrors: validationErrors.length > 0 ? validationErrors : undefined,
                triggerSource,
            };
            // Check alert patterns
            if (checkAlertPatterns(eventName)) {
                playAlertSound();
            }
            return [...prev.slice(-(maxEvents - 1)), newEvent];
        });
    }, [checkDuplicate, validateEvent, captureTriggerSource, checkAlertPatterns, playAlertSound, maxEvents]);
    // Keep ref updated with latest callback - set immediately, not in effect
    addEventRef.current = addEvent;
    // Intercept network requests for analytics endpoints
    useEffect(() => {
        if (!isCapturing)
            return;
        const originalFetch = window.fetch;
        // Monkey-patch window.fetch for analytics interception
        const patchedFetch = async function (input, init) {
            const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
            const isAnalytics = url.includes('google-analytics') ||
                url.includes('googletagmanager') ||
                url.includes('analytics.google') ||
                url.includes('posthog') ||
                isPosthogProxyUrl(url) ||
                url.includes('gtm') ||
                url.includes('collect?');
            if (isAnalytics) {
                const startTime = Date.now();
                const request = {
                    url,
                    method: init?.method || 'GET',
                    timestamp: new Date(),
                };
                try {
                    const response = await originalFetch.apply(this, [input, init]);
                    request.status = response.status;
                    request.duration = Date.now() - startTime;
                    setNetworkRequests(prev => [...prev.slice(-50), request]);
                    return response;
                }
                catch (error) {
                    request.duration = Date.now() - startTime;
                    setNetworkRequests(prev => [...prev.slice(-50), request]);
                    throw error;
                }
            }
            return originalFetch.apply(this, [input, init]);
        };
        window.fetch = patchedFetch;
        return () => {
            window.fetch = originalFetch;
        };
    }, [isCapturing]);
    // Track which dataLayer events we've already captured
    const capturedDataLayerIndices = useRef(new Set());
    // Intercept GTM dataLayer - runs once on mount
    useEffect(() => {
        if (!isCapturing)
            return;
        window.dataLayer = window.dataLayer || [];
        const originalPush = window.dataLayer.push.bind(window.dataLayer);
        // Capture any existing events we haven't seen yet
        for (let i = 0; i < window.dataLayer.length; i++) {
            if (!capturedDataLayerIndices.current.has(i)) {
                capturedDataLayerIndices.current.add(i);
                const data = window.dataLayer[i];
                if (!data)
                    continue;
                const eventName = extractDataLayerEventName(data);
                addEventRef.current?.('gtm', eventName || 'dataLayer.push', data);
            }
        }
        // Intercept new pushes
        window.dataLayer.push = function (...args) {
            for (const data of args) {
                const eventName = extractDataLayerEventName(data);
                addEventRef.current?.('gtm', eventName || 'dataLayer.push', data);
            }
            return originalPush(...args);
        };
        return () => {
            if (window.dataLayer) {
                window.dataLayer.push = originalPush;
            }
        };
    }, [isCapturing]); // Removed addEvent from deps - using ref instead
    useEffect(() => {
        if (!isCapturing)
            return;
        let disposed = false;
        let unsubscribe;
        fireAndForget(async () => {
            const { subscribeAnalyticsEvents } = await import('@sandstone/analytics/client');
            if (disposed)
                return;
            unsubscribe = subscribeAnalyticsEvents(event => {
                addEventRef.current?.('posthog', event.event, event.properties || {});
            });
        });
        return () => {
            disposed = true;
            unsubscribe?.();
        };
    }, [isCapturing]);
    const clearEvents = useCallback(() => {
        setEvents([]);
        setNetworkRequests([]);
        sessionStorage.removeItem(STORAGE_KEY_EVENTS);
    }, []);
    const toggleCapturing = useCallback(() => {
        setIsCapturing(prev => !prev);
    }, []);
    const updateSettings = useCallback((updates) => {
        setSettings(prev => ({ ...prev, ...updates }));
    }, []);
    const exportEvents = useCallback(() => {
        const exportData = {
            exportedAt: new Date().toISOString(),
            eventCount: events.length,
            events: events.map(e => ({
                ...e,
                timestamp: e.timestamp.toISOString(),
            })),
            networkRequests: networkRequests.map(r => ({
                ...r,
                timestamp: r.timestamp.toISOString(),
            })),
        };
        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `analytics-events-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, [events, networkRequests]);
    return {
        events,
        networkRequests,
        isCapturing,
        settings,
        toggleCapturing,
        clearEvents,
        updateSettings,
        exportEvents,
    };
}
